import React, { useEffect, useState, useCallback } from "react";
import { Files, Loader2, Trash2, ArrowRight, RefreshCw, Search } from "lucide-react";
import { helperListTemplates, helperDeleteTemplate } from "../lib/api";
import { toast } from "sonner";

export default function Templates({ userId, onLoad }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await helperListTemplates(userId); setTemplates(r.templates || []); }
    catch (e) { toast.error("Could not load your templates"); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await helperDeleteTemplate(t.id, userId); setTemplates((p) => p.filter((x) => x.id !== t.id)); toast.success("Deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch (e) { return ""; } };
  const ql = q.trim().toLowerCase();
  const shown = ql ? templates.filter((t) => (t.name || "").toLowerCase().includes(ql) || (t.body || "").toLowerCase().includes(ql)) : templates;

  return (
    <div className="lg:col-span-2 nx-glass rounded-2xl p-4 space-y-3" data-testid="templates-panel">
      <div className="flex items-center gap-2">
        <Files className="w-4 h-4 text-[#00F0FF]" />
        <p className="text-cyan-200/70 text-xs nx-mono tracking-widest">TEMPLATES — {shown.length}{ql ? ` / ${templates.length}` : ""} saved</p>
        <button onClick={load} data-testid="templates-refresh" className="ml-auto text-cyan-200/50 hover:text-[#00F0FF]"><RefreshCw className="w-4 h-4" /></button>
      </div>
      <p className="text-cyan-100/40 text-[11px] nx-mono">Generate a letter, then hit <b className="text-cyan-100/60">Template</b> to save it. Load one here to reuse — tweak names/dates and re-sign.</p>

      <div className="relative">
        <Search className="w-4 h-4 text-cyan-200/40 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="templates-search"
          placeholder="Search templates by name or text…"
          className="w-full bg-[#0a1526] text-cyan-50 border border-[#00F0FF33] rounded-xl pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]" />
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
      ) : shown.length === 0 ? (
        <div className="py-16 flex flex-col items-center justify-center text-center text-cyan-100/30 gap-2">
          <Files className="w-8 h-8" />
          <p className="text-sm nx-mono">{ql ? "No matches — try another search" : "No templates yet. Save a finished letter to reuse it later."}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="templates-list">
          {shown.map((t) => (
            <div key={t.id} data-testid="template-item" className="flex items-center gap-3 p-3 rounded-xl bg-[#0a1526] border border-[#00F0FF22] hover:border-[#00F0FF55] transition-colors">
              <div className="w-9 h-9 rounded-lg bg-[#00F0FF11] flex items-center justify-center shrink-0"><Files className="w-4 h-4 text-[#00F0FF]" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-cyan-50 text-sm font-semibold truncate">{t.name}</p>
                <p className="text-cyan-100/40 text-[11px] nx-mono truncate">{[t.task, fmtDate(t.created_at)].filter(Boolean).join(" · ")} — {(t.body || "").slice(0, 80)}…</p>
              </div>
              <button onClick={() => onLoad?.(t)} data-testid="template-load" className="px-3 py-1.5 rounded-lg nx-mono text-[11px] text-[#030712] bg-[#00F0FF] hover:opacity-90 flex items-center gap-1 whitespace-nowrap">Use <ArrowRight className="w-3 h-3" /></button>
              <button onClick={() => remove(t)} data-testid="template-delete" title="Delete" className="p-2 rounded-lg text-cyan-100/40 hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
